// src/app/services/filterApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';
import { IFilter, IFilterParams, UpdatePayload } from '../models';

export const filterApi = createApi({
  reducerPath: 'filterApi',
  baseQuery,
  endpoints: (builder) => ({
    getFilterParams: builder.query<{ filter: IFilterParams }, void>({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/filters/params/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getFilter: builder.query<IFilter, number>({
      query: (id) => `/api/v1.0/ahu/filters/${id}`,
      transformResponse: (data: IFilter) => {
        data.params = data.filter_params ? JSON.parse(data.filter_params) : {};
        data.frameParams = data.frame_params
          ? JSON.parse(data.frame_params)
          : {};
        return data;
      },
    }),
    getAllFiltersCount: builder.query<{ count: number }, void>({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/filters/all/count/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getFiltersCount: builder.query<{ count: number }, string>({
      query: (queryString) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/filters/all/count/?${queryString}`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getAllFramesCount: builder.query<{ count: number }, void>({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/filters/frames/all/count/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getFramesCount: builder.query<{ count: number }, string>({
      query: (queryString) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/filters/frames/all/count/?${queryString}`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getFilterFrames: builder.query<any[], number>({
      query: (filterId) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/filters/frames/all/?page=1&filter_id=${filterId}`,
      extraOptions: {
        cacheData: true,
      },
    }),
    updateFilter: builder.mutation<IFilter, UpdatePayload<IFilter>>({
      query: ({ id, body, signal }) => ({
        url: `/api/v1.0/ahu/filters/${id}`,
        method: 'PATCH',
        body,
        signal,
      }),
      transformResponse: (data: IFilter) => {
        data.params = data.filter_params ? JSON.parse(data.filter_params) : {};
        data.frameParams = data.frame_params
          ? JSON.parse(data.frame_params)
          : {};
        return data;
      },
    }),
    createFilter: builder.mutation<any, UpdatePayload<any>>({
      query: ({ body, signal }) => ({
        url: `/api/v1.0/ahu/filters/`,
        method: 'POST',
        body,
        signal,
      }),
    }),
    getFilters: builder.query<any[], string>({
      query: (queryString) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/filters/all/?${queryString}`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getFrameLoadings: builder.query<any[], string>({
      query: (queryString) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/filters/loadings/?${queryString}`,
      extraOptions: {
        cacheData: true,
      },
    }),
  }),
});

export const {
  useLazyGetFilterQuery,
  useGetFilterParamsQuery,
  useLazyGetFiltersCountQuery,
  useGetAllFiltersCountQuery,
  useGetAllFramesCountQuery,
  useLazyGetFramesCountQuery,
  useLazyGetFilterFramesQuery,
  useUpdateFilterMutation,
  useCreateFilterMutation,
  useLazyGetFiltersQuery,
  useGetFrameLoadingsQuery,
  useLazyGetFrameLoadingsQuery,
} = filterApi;
